import React, { useState, useEffect } from 'react';
import { Box, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Select, FormControl, InputLabel, Grid, CircularProgress, Alert, Chip, FormControlLabel, Checkbox } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Popover from '@mui/material/Popover';
import { supabase } from '../../services/supabaseClient';
import { fetchWorkers } from '../../services/workerService';
import type { Worker } from '../../types/worker';

const statusOptions = ['pending', 'in_progress', 'completed', 'on-hold'];
const categoryOptions = [
  'Crop Management',
  'Animal Care',
  'Equipment Maintenance',
  'Infrastructure',
  'General',
];
const priorityOptions = ['low', 'medium', 'high', 'urgent'];
const frequencyOptions = ['once', 'daily', 'weekly', 'monthly'];

const TasksTab: React.FC<{ farmId: string }> = ({ farmId }) => {
  const [open, setOpen] = useState(false);
  const [tasks, setTasks] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [form, setForm] = useState({
    workerIds: [],
    title: '',
    description: '',
    category: '',
    priority: 'medium',
    status: 'pending',
    frequency: 'once',
    is_mandatory: false,
    dueDate: '',
    checklist: [''],
    notes: '',
    time_slots: [''],
  });
  const [submitting, setSubmitting] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<any>(null);
  const [editForm, setEditForm] = useState<any>(null);
  const [popoverAnchor, setPopoverAnchor] = useState<null | HTMLElement>(null);
  const [popoverTask, setPopoverTask] = useState<any>(null);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      supabase.from('tasks').select('id, title, due_date, status, assigned_workers'),
      fetchWorkers()
    ])
      .then(([taskRes, workersRes]) => {
        if (taskRes.error) throw taskRes.error;
        setTasks(taskRes.data || []);
        setWorkers(workersRes);
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const handleFormChange = (field: string, value: any) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleChecklistChange = (idx: number, value: string) => {
    setForm(prev => ({ ...prev, checklist: prev.checklist.map((item, i) => i === idx ? value : item) }));
  };

  const handleAddChecklistItem = () => {
    setForm(prev => ({ ...prev, checklist: [...prev.checklist, ''] }));
  };

  const handleRemoveChecklistItem = (idx: number) => {
    setForm(prev => ({ ...prev, checklist: prev.checklist.filter((_, i) => i !== idx) }));
  };

  const handleTimeSlotsChange = (idx: number, value: string) => {
    setForm(prev => ({ ...prev, time_slots: prev.time_slots.map((item, i) => i === idx ? value : item) }));
  };

  const handleAddTimeSlot = () => {
    setForm(prev => ({ ...prev, time_slots: [...prev.time_slots, ''] }));
  };

  const handleRemoveTimeSlot = (idx: number) => {
    setForm(prev => ({ ...prev, time_slots: prev.time_slots.filter((_, i) => i !== idx) }));
  };

  const handleAddTask = async () => {
    setSubmitting(true);
    setError(null);
    try {
      if (!form.workerIds.length || !form.title || !form.dueDate || !form.description) {
        setError('Worker(s), title, description, and due date are required.');
        setSubmitting(false);
        return;
      }
      const payload: any = {
        farm_id: farmId,
        title: form.title,
        description: form.description,
        category: form.category,
        priority: form.priority,
        status: form.status,
        frequency: form.frequency,
        is_mandatory: form.is_mandatory,
        due_date: form.dueDate,
        assigned_workers: form.workerIds,
        checklist: form.checklist.filter(Boolean).map((text: string, i: number) => ({ id: i + 1, text, completed: false })),
        notes: form.notes,
        time_slots: form.time_slots.filter(Boolean),
      };
      if (form.status === 'completed') {
        payload.completed_date = new Date().toISOString();
      }
      const { error: insertError } = await supabase.from('tasks').insert([payload]);
      if (insertError) throw insertError;
      // Refresh tasks
      const { data: taskData, error: taskFetchError } = await supabase.from('tasks').select('*');
      if (taskFetchError) throw taskFetchError;
      setTasks(taskData || []);
      setOpen(false);
      setForm({
        workerIds: [],
        title: '',
        description: '',
        category: '',
        priority: 'medium',
        status: 'pending',
        frequency: 'once',
        is_mandatory: false,
        dueDate: '',
        checklist: [''],
        notes: '',
        time_slots: [''],
      });
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // CRUD: Edit
  const handleEditClick = async (task: any) => {
    try {
      const { data, error } = await supabase.from('tasks').select('*').eq('id', task.id).single();
      if (error) throw error;
      setEditForm({
        ...data,
        workerIds: data.assigned_workers || [],
        time_slots: data.time_slots || [''],
        checklist: data.checklist || [],
      });
      setSelectedTask(data);
      setEditDialogOpen(true);
    } catch (err: any) {
      setError(err.message);
    }
  };
  const handleEditFormChange = (field: string, value: any) => {
    setEditForm((prev: any) => ({ ...prev, [field]: value }));
  };
  const handleEditChecklistChange = (idx: number, value: string) => {
    setEditForm((prev: any) => ({ ...prev, checklist: prev.checklist.map((item: any, i: number) => i === idx ? { ...item, text: value } : item) }));
  };
  const handleEditChecklistToggle = (idx: number) => {
    setEditForm((prev: any) => ({ ...prev, checklist: prev.checklist.map((item: any, i: number) => i === idx ? { ...item, completed: !item.completed } : item) }));
  };
  const handleEditChecklistRemove = (idx: number) => {
    setEditForm((prev: any) => ({ ...prev, checklist: prev.checklist.filter((_: any, i: number) => i !== idx) }));
  };
  const handleEditChecklistAdd = () => {
    setEditForm((prev: any) => ({ ...prev, checklist: [...(prev.checklist || []), { id: (prev.checklist?.length || 0) + 1, text: '', completed: false }] }));
  };
  const handleEditTaskSubmit = async () => {
    try {
      const payload: any = {
        title: editForm.title,
        description: editForm.description,
        category: editForm.category,
        priority: editForm.priority,
        status: editForm.status,
        frequency: editForm.frequency,
        is_mandatory: editForm.is_mandatory,
        due_date: editForm.due_date,
        assigned_workers: editForm.workerIds,
        checklist: editForm.checklist,
        notes: editForm.notes,
        time_slots: editForm.time_slots,
      };
      if (editForm.status === 'completed') {
        payload.completed_date = new Date().toISOString();
      }
      const { error: updateError } = await supabase.from('tasks').update(payload).eq('id', selectedTask.id);
      if (updateError) throw updateError;
      const { data: taskData, error: taskFetchError } = await supabase.from('tasks').select('*');
      if (taskFetchError) throw taskFetchError;
      setTasks(taskData || []);
      setEditDialogOpen(false);
      setSelectedTask(null);
    } catch (err: any) {
      setError(err.message);
    }
  };

  // CRUD: Delete
  const handleDeleteClick = (task: any) => {
    setSelectedTask(task);
    setDeleteDialogOpen(true);
  };
  const handleDeleteTask = async () => {
    try {
      const { error: deleteError } = await supabase.from('tasks').delete().eq('id', selectedTask.id);
      if (deleteError) throw deleteError;
      const { data: taskData, error: taskFetchError } = await supabase.from('tasks').select('*');
      if (taskFetchError) throw taskFetchError;
      setTasks(taskData || []);
      setDeleteDialogOpen(false);
      setSelectedTask(null);
    } catch (err: any) {
      setError(err.message);
    }
  };

  // Popover for details on click
  const handleRowClick = (event: React.MouseEvent<HTMLElement>, task: any) => {
    setPopoverAnchor(event.currentTarget);
    setPopoverTask(task);
  };
  const handlePopoverClose = () => {
    setPopoverAnchor(null);
    setPopoverTask(null);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">Worker Tasks</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpen(true)}>Assign Task</Button>
      </Box>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}><CircularProgress /></Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Due Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Assigned Worker</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tasks.map(row => (
                <TableRow
                  key={row.id}
                  hover
                  onClick={e => handleRowClick(e, row)}
                  style={{ cursor: 'pointer' }}
                >
                  <TableCell>{row.title}</TableCell>
                  <TableCell>{row.due_date?.split('T')[0]}</TableCell>
                  <TableCell>
                    <Chip label={row.status} color={row.status === 'completed' ? 'success' : row.status === 'in_progress' ? 'info' : row.status === 'on-hold' ? 'warning' : 'default'} size="small" />
                  </TableCell>
                  <TableCell>
                    {row.assigned_workers && row.assigned_workers.length > 0
                      ? row.assigned_workers.map((id: string) => workers.find(w => w.id === id)?.name || id).join(', ')
                      : 'Unassigned'}
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Edit Task">
                      <IconButton onClick={e => { e.stopPropagation(); handleEditClick(row); }} size="small"><EditIcon /></IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Task">
                      <IconButton onClick={e => { e.stopPropagation(); handleDeleteClick(row); }} size="small"><DeleteIcon /></IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Popover
        open={Boolean(popoverAnchor)}
        anchorEl={popoverAnchor}
        onClose={handlePopoverClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Box sx={{ p: 2, minWidth: 300 }}>
          {popoverTask && (
            <>
              <Typography variant="subtitle1">{popoverTask.title}</Typography>
              <Typography variant="body2" color="text.secondary">{popoverTask.description}</Typography>
              <Typography variant="body2">Category: {popoverTask.category}</Typography>
              <Typography variant="body2">Priority: {popoverTask.priority}</Typography>
              <Typography variant="body2">Status: {popoverTask.status}</Typography>
              <Typography variant="body2">Due: {popoverTask.due_date?.split('T')[0]}</Typography>
              <Typography variant="body2">Notes: {popoverTask.notes}</Typography>
              <Typography variant="body2">Checklist:</Typography>
              <ul>
                {(popoverTask.checklist || []).map((item: any, idx: number) => (
                  <li key={idx} style={{ textDecoration: item.completed ? 'line-through' : undefined }}>{item.text}</li>
                ))}
              </ul>
            </>
          )}
        </Box>
      </Popover>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Assign Task</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Worker(s)</InputLabel>
                <Select
                  multiple
                  value={form.workerIds}
                  label="Worker(s)"
                  onChange={e => handleFormChange('workerIds', e.target.value)}
                  renderValue={selected => selected.map((id: string) => workers.find(w => w.id === id)?.name || id).join(', ')}
                >
                  {workers.map(w => (
                    <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Title"
                value={form.title}
                onChange={e => handleFormChange('title', e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={form.description}
                onChange={e => handleFormChange('description', e.target.value)}
                required
                multiline
                minRows={2}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={form.category}
                  label="Category"
                  onChange={e => handleFormChange('category', e.target.value)}
                >
                  {categoryOptions.map(opt => (
                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={form.priority}
                  label="Priority"
                  onChange={e => handleFormChange('priority', e.target.value)}
                >
                  {priorityOptions.map(opt => (
                    <MenuItem key={opt} value={opt}>{opt.charAt(0).toUpperCase() + opt.slice(1)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={form.status}
                  label="Status"
                  onChange={e => handleFormChange('status', e.target.value)}
                >
                  {statusOptions.map(opt => (
                    <MenuItem key={opt} value={opt}>{opt.replace('_', ' ').toUpperCase()}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Frequency</InputLabel>
                <Select
                  value={form.frequency}
                  label="Frequency"
                  onChange={e => handleFormChange('frequency', e.target.value)}
                >
                  {frequencyOptions.map(opt => (
                    <MenuItem key={opt} value={opt}>{opt.charAt(0).toUpperCase() + opt.slice(1)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={<Checkbox checked={form.is_mandatory} onChange={e => handleFormChange('is_mandatory', e.target.checked)} />}
                label="Mandatory Task"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Due Date"
                type="date"
                value={form.dueDate}
                onChange={e => handleFormChange('dueDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mt: 2 }}>Checklist</Typography>
              {form.checklist.map((item, idx) => (
                <Box key={idx} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <TextField
                    fullWidth
                    value={item}
                    onChange={e => handleChecklistChange(idx, e.target.value)}
                    placeholder={`Checklist item #${idx + 1}`}
                  />
                  <Button onClick={() => handleRemoveChecklistItem(idx)} disabled={form.checklist.length === 1}>Remove</Button>
                </Box>
              ))}
              <Button onClick={handleAddChecklistItem}>Add Checklist Item</Button>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                value={form.notes}
                onChange={e => handleFormChange('notes', e.target.value)}
                multiline
                minRows={2}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mt: 2 }}>Time Slots</Typography>
              {form.time_slots.map((item, idx) => (
                <Box key={idx} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <TextField
                    fullWidth
                    value={item}
                    onChange={e => handleTimeSlotsChange(idx, e.target.value)}
                    placeholder={`Time slot #${idx + 1}`}
                  />
                  <Button onClick={() => handleRemoveTimeSlot(idx)} disabled={form.time_slots.length === 1}>Remove</Button>
                </Box>
              ))}
              <Button onClick={handleAddTimeSlot}>Add Time Slot</Button>
            </Grid>
          </Grid>
          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddTask} disabled={submitting}>
            {submitting ? <CircularProgress size={20} /> : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Task</DialogTitle>
        <DialogContent>
          {editForm && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Worker(s)</InputLabel>
                  <Select
                    multiple
                    value={editForm.workerIds}
                    label="Worker(s)"
                    onChange={e => handleEditFormChange('workerIds', e.target.value)}
                    renderValue={selected => selected.map((id: string) => workers.find(w => w.id === id)?.name || id).join(', ')}
                  >
                    {workers.map(w => (
                      <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Title"
                  value={editForm.title}
                  onChange={e => handleEditFormChange('title', e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  value={editForm.description}
                  onChange={e => handleEditFormChange('description', e.target.value)}
                  required
                  multiline
                  minRows={2}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={editForm.category}
                    label="Category"
                    onChange={e => handleEditFormChange('category', e.target.value)}
                  >
                    {categoryOptions.map(opt => (
                      <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Priority</InputLabel>
                  <Select
                    value={editForm.priority}
                    label="Priority"
                    onChange={e => handleEditFormChange('priority', e.target.value)}
                  >
                    {priorityOptions.map(opt => (
                      <MenuItem key={opt} value={opt}>{opt.charAt(0).toUpperCase() + opt.slice(1)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={editForm.status}
                    label="Status"
                    onChange={e => handleEditFormChange('status', e.target.value)}
                  >
                    {statusOptions.map(opt => (
                      <MenuItem key={opt} value={opt}>{opt.replace('_', ' ').toUpperCase()}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Frequency</InputLabel>
                  <Select
                    value={editForm.frequency}
                    label="Frequency"
                    onChange={e => handleEditFormChange('frequency', e.target.value)}
                  >
                    {frequencyOptions.map(opt => (
                      <MenuItem key={opt} value={opt}>{opt.charAt(0).toUpperCase() + opt.slice(1)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={<Checkbox checked={editForm.is_mandatory} onChange={e => handleEditFormChange('is_mandatory', e.target.checked)} />}
                  label="Mandatory Task"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Due Date"
                  type="date"
                  value={editForm.due_date}
                  onChange={e => handleEditFormChange('due_date', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mt: 2 }}>Checklist</Typography>
                {(editForm.checklist || []).map((item: any, idx: number) => (
                  <Box key={idx} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Checkbox checked={item.completed} onChange={() => handleEditChecklistToggle(idx)} />
                    <TextField
                      fullWidth
                      value={item.text}
                      onChange={e => handleEditChecklistChange(idx, e.target.value)}
                      placeholder={`Checklist item #${idx + 1}`}
                    />
                    <Button onClick={() => handleEditChecklistRemove(idx)} disabled={(editForm.checklist?.length || 1) === 1}>Remove</Button>
                  </Box>
                ))}
                <Button onClick={handleEditChecklistAdd}>Add Checklist Item</Button>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes"
                  value={editForm.notes}
                  onChange={e => handleEditFormChange('notes', e.target.value)}
                  multiline
                  minRows={2}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mt: 2 }}>Time Slots</Typography>
                {(editForm.time_slots || []).map((item: string, idx: number) => (
                  <Box key={idx} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <TextField
                      fullWidth
                      value={item}
                      onChange={e => handleTimeSlotsChange(idx, e.target.value)}
                      placeholder={`Time slot #${idx + 1}`}
                    />
                    <Button onClick={() => handleRemoveTimeSlot(idx)} disabled={editForm.time_slots.length === 1}>Remove</Button>
                  </Box>
                ))}
                <Button onClick={handleAddTimeSlot}>Add Time Slot</Button>
              </Grid>
            </Grid>
          )}
          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleEditTaskSubmit} disabled={submitting}>
            {submitting ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Task</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this task?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteTask} disabled={submitting}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TasksTab; 